import {
  LayerProps,
  DataDrivenPropertyValueSpecification,
} from 'shared/map-exports';
import { Theme } from '@mui/material';
import { decomposeColor, hslToRgb } from '@mui/material/styles';
import { CSSProperties } from 'react';
import { Capacitor } from '@capacitor/core';
import { Altitude } from 'shared/types';
import { isSafari } from 'shared/utils';
import { AircraftIconVariant } from './types';
import { TOOLTIP_LABEL_LAYOUT_STYLES } from '../../../mapping.styles';

export const TAIL_NUM_VISIBLE_ZOOM_LEVEL = 10; // ~3 miles

export const DEFAULT_FILL_COLOR = '#A750FF';
export const DEFAULT_STROKE_COLOR = '#1F1F1F';
export const ACTIVE_FILL_COLOR = '#4AFB2D';
export const GROUND_FILL_COLOR = '#999999';

export const MIN_ICON_SCALE = 0.2;
export const MAX_ICON_SCALE = 0.35;

export const STROKE_LINE_WIDTH = 2.5;

// The pixel offset range to devote to altitude-based shadow offset.
// This number is divided by SHADOW_DISTANCE_ALTITUDE_MAX_FEET and then
// added to SHADOW_DISTANCE_MIN_PX to calculate the final offset.
export const SHADOW_DISTANCE_BY_ALTITUDE_RANGE_PX = 8;

// The maximum altitude to use when calculating the shadow offset.
// Any value greater than this will be offset at SHADOW_DISTANCE_BY_ALTITUDE_RANGE_PX.
export const SHADOW_DISTANCE_ALTITUDE_MAX_FEET = 18000;

// The minimum offset distance, used as a constant to add to the offset calculated
// from altitude.
export const SHADOW_DISTANCE_MIN_PX = 3;

const SHADOW_BLUR_PX = 1.5;
const SHADOW_COLOR = 'rgba(50,50,50,0.5)';

/** `SHADOW_COLOR` as deck.gl expects it: 0-255 RGBA. */
export const SHADOW_COLOR_RGBA: [number, number, number, number] = [
  50, 50, 50, 128,
];

// Zoom range across which icons scale between MIN_ICON_SCALE and MAX_ICON_SCALE.
export const ICON_SCALE_MIN_ZOOM = 6;
export const ICON_SCALE_MAX_ZOOM = 10;

/**
 * Icon scale for a zoom level: pinned at the extremes, linear in between.
 */
export const getIconScaleForZoom = (zoom: number): number => {
  if (zoom <= ICON_SCALE_MIN_ZOOM) {
    return MIN_ICON_SCALE;
  }
  if (zoom >= ICON_SCALE_MAX_ZOOM) {
    return MAX_ICON_SCALE;
  }
  const progress =
    (zoom - ICON_SCALE_MIN_ZOOM) / (ICON_SCALE_MAX_ZOOM - ICON_SCALE_MIN_ZOOM);
  return MIN_ICON_SCALE + progress * (MAX_ICON_SCALE - MIN_ICON_SCALE);
};

/**
 * WebKit calculates `drop-shadow()` once and then rotates the cached result
 * along with the aircraft, so the shadow ends up pointing the wrong way.
 * Forcing GPU compositing makes it recalculate as the aircraft moves.
 *
 * Chromium (desktop Chrome, Android WebView) and Gecko don't have the bug, and
 * promoting every marker to its own compositor layer there costs real GPU
 * memory once hundreds of aircraft are on screen — so only apply it on WebKit.
 *
 * See:
 * * https://github.com/mdn/browser-compat-data/issues/17726
 * * https://stackoverflow.com/questions/10814178/css-performance-relative-to-translatez0
 */
const NEEDS_WEBKIT_SHADOW_HACK =
  isSafari() || Capacitor.getPlatform() === 'ios';

export type AircraftShadowStyleArgs = {
  rotate: number;
  altitude?: Altitude;
  scale: number;
};

/** Screen-space direction the shadow falls in: down and to the right. */
const SHADOW_SCREEN_ANGLE_RAD = Math.PI / 4;

/**
 * How far the shadow sits from the aircraft, in pixels. Grows with altitude and
 * shrinks as the user zooms out; zero for grounded or unknown-altitude craft.
 */
export const getAircraftShadowDistancePx = (
  args: Pick<AircraftShadowStyleArgs, 'altitude' | 'scale'>,
): number => {
  const { altitude, scale } = args;

  if (altitude === 'ground' || altitude == null) {
    return 0;
  }

  // The shadow should get "shorter" as the user zooms out.
  const scaleOffsetMultipler =
    ((scale - MIN_ICON_SCALE) / (MAX_ICON_SCALE - MIN_ICON_SCALE) / 3) * 2 +
    0.33;
  return Math.ceil(
    (altitude / SHADOW_DISTANCE_ALTITUDE_MAX_FEET) *
      SHADOW_DISTANCE_BY_ALTITUDE_RANGE_PX *
      scaleOffsetMultipler +
      SHADOW_DISTANCE_MIN_PX,
  );
};

/**
 * Shadow offset for the DOM markers.
 *
 * The `filter` is applied inside the marker element, which Mapbox has already
 * rotated by the aircraft's heading, so the offset has to counter-rotate to
 * land in a fixed screen direction.
 */
export const getAircraftShadowOffsetPx = (
  args: AircraftShadowStyleArgs,
): [number, number] => {
  const { rotate } = args;
  const shadowDistance = getAircraftShadowDistancePx(args);

  const angleRad = (-1 * rotate * Math.PI) / 180 + SHADOW_SCREEN_ANGLE_RAD;
  return [
    Math.ceil(Math.cos(angleRad) * shadowDistance),
    Math.ceil(Math.sin(angleRad) * shadowDistance),
  ];
};

/**
 * Shadow offset for the deck.gl layer.
 *
 * Unlike the DOM markers, `getPixelOffset` is applied in screen space rather
 * than inside the icon's rotated frame, so there is no heading to cancel out
 * and the direction is simply constant.
 */
export const getAircraftShadowPixelOffset = (
  args: Pick<AircraftShadowStyleArgs, 'altitude' | 'scale'>,
): [number, number] => {
  const shadowDistance = getAircraftShadowDistancePx(args);
  return [
    Math.ceil(Math.cos(SHADOW_SCREEN_ANGLE_RAD) * shadowDistance),
    Math.ceil(Math.sin(SHADOW_SCREEN_ANGLE_RAD) * shadowDistance),
  ];
};

/**
 * Builds the aircraft's drop-shadow as a plain style object. This is applied
 * inline rather than through `makeStyles` on purpose: the shadow varies with
 * heading, altitude and zoom.
 */
export const getAircraftShadowStyle = (
  args: AircraftShadowStyleArgs,
): CSSProperties => {
  const [offsetX, offsetY] = getAircraftShadowOffsetPx(args);

  return {
    filter: `drop-shadow(${offsetX}px ${offsetY}px ${SHADOW_BLUR_PX}px ${SHADOW_COLOR})`,
    ...(NEEDS_WEBKIT_SHADOW_HACK
      ? { WebkitTransform: 'translate3d(0,0,0)' }
      : {}),
  };
};

export const fillColorForVariant = (variant: AircraftIconVariant): string => {
  if (variant === 'active' || variant === 'activeground') {
    return ACTIVE_FILL_COLOR;
  }
  if (variant === 'ground') {
    return GROUND_FILL_COLOR;
  }
  return DEFAULT_FILL_COLOR;
};

// Show a label when the map is zoomed in past TAIL_NUM_VISIBLE_ZOOM_LEVEL, OR
// whenever the aircraft is hovered/selected (regardless of zoom).
const buildLabelOpacityExpression = (
  activeHexCodes: string[],
): DataDrivenPropertyValueSpecification<number> => [
  'step',
  ['zoom'],
  // Below the zoom threshold: only the hovered/selected aircraft's label shows.
  ['case', ['in', ['get', 'hex'], ['literal', activeHexCodes]], 1, 0],
  TAIL_NUM_VISIBLE_ZOOM_LEVEL,
  // At/above the threshold: all labels show.
  1,
];

// The label's vertical gap tracks the icon's zoom scale so it stays a
// consistent visual distance above the (shrinking) icon. This mirrors the
// icon scaling in AircraftIconMarkers: MIN_ICON_SCALE at/below zoom 6 up to
// MAX_ICON_SCALE at/above TAIL_NUM_VISIBLE_ZOOM_LEVEL (interpolate clamps
// outside that range, matching the icon's pinned scale). Offset is in ems.
const LABEL_OFFSET_MAX_EM = -3.5; // at MAX_ICON_SCALE
const LABEL_OFFSET_MIN_EM =
  LABEL_OFFSET_MAX_EM * (MIN_ICON_SCALE / MAX_ICON_SCALE);

const labelOffsetExpression: DataDrivenPropertyValueSpecification<
  [number, number]
> = [
  'interpolate',
  ['linear'],
  ['zoom'],
  6,
  ['literal', [0, LABEL_OFFSET_MIN_EM]],
  TAIL_NUM_VISIBLE_ZOOM_LEVEL,
  ['literal', [0, LABEL_OFFSET_MAX_EM]],
];

/** Matches `TOOLTIP_LABEL_LAYOUT_STYLES`' `text-size`. */
export const LABEL_FONT_SIZE_PX = 11;

/**
 * Chip padding for the deck.gl label layer, as `[left, top, right, bottom]`.
 *
 * The Mapbox layer expressed the same thing as `icon-text-fit-padding`, which
 * is ordered `[top, right, bottom, left]`.
 */
export const LABEL_BACKGROUND_PADDING_PX: [number, number, number, number] = [
  6, 4, 6, 3,
];

/** Rounds the chip, standing in for the SDF background image's corners. */
export const LABEL_BACKGROUND_RADIUS_PX = 4;

/**
 * Gap between the aircraft and its label, in screen pixels.
 *
 * The Mapbox layer interpolated an em offset over the same zoom range the icon
 * scale uses, which is just the offset tracking the icon's size. Driving it off
 * `scale` directly says that outright, and keeps the label a consistent visual
 * distance above a shrinking icon.
 */
export const getAircraftLabelPixelOffset = (
  scale: number,
): [number, number] => [
  0,
  LABEL_OFFSET_MAX_EM * (scale / MAX_ICON_SCALE) * LABEL_FONT_SIZE_PX,
];

/**
 * Converts a theme colour into the `[r, g, b, a]` deck.gl wants, with alpha on
 * the same 0-255 scale as the channels.
 *
 * `decomposeColor` handles hex and rgb/rgba but reports hsl in its own units,
 * so those are converted first.
 */
export const cssColorToDeckColor = (
  color: string,
): [number, number, number, number] => {
  const { values } = decomposeColor(
    color.startsWith('hsl') ? hslToRgb(color) : color,
  );
  const [red, green, blue, alpha = 1] = values;
  return [red, green, blue, Math.round(alpha * 255)];
};

export const getAircraftLabelStyles = (
  theme: Theme,
  activeHexCodes: string[],
): LayerProps => {
  const labelOpacityExpression = buildLabelOpacityExpression(activeHexCodes);
  return {
    type: 'symbol',
    layout: {
      ...TOOLTIP_LABEL_LAYOUT_STYLES,
      'text-field': ['get', 'label'],
      // Float the label above the aircraft icon, scaling the gap with the icon.
      'text-anchor': 'bottom',
      'text-offset': labelOffsetExpression,
      'text-allow-overlap': true,
      'icon-text-fit': 'both',
      'icon-text-fit-padding': [4, 6, 4, 6], // [top, right, bottom, left]
      'icon-allow-overlap': true,
    },
    paint: {
      // The chip background tints the SDF LabelTooltipBackground image.
      'icon-color': theme.palette.background.paper,
      'text-color': theme.palette.text.primary,
      'text-opacity': labelOpacityExpression,
      'icon-opacity': labelOpacityExpression,
    },
    // Don't render a chip for aircraft with no tail number / callsign.
    filter: ['!=', ['get', 'label'], ''],
  };
};

export const traceStyle: LayerProps = {
  type: 'line',
  source: 'route',
  layout: {
    'line-join': 'round',
    'line-cap': 'round',
  },
  paint: {
    'line-color': '#4AFB2D',
    'line-width': ['interpolate', ['linear'], ['zoom'], 6, 1.5, 12, 4],
    'line-gradient': [
      'interpolate',
      ['exponential', 0.8],
      ['line-progress'],
      0,
      'rgba(74,251,45,0.3)',
      1,
      'rgba(74,251,45,0.6)',
    ],
  },
};
